import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  setDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';

import { FirebaseService, SYNC_SCHEMA_VERSION } from './firebase.service.js';
import { SavedChannel, StreamProject } from '../models/library.model.js';
import { SavedState } from './twitch-chat-state.service.js';
import { checkDemoMode } from './demo.service.js';
import { RemoteState, canOverwrite, newRev } from '../utils/sync-reconcile.js';

export interface GlobalSettings {
  interfaceScale?: number;
  isChatOverlayEnabled?: boolean;
  chatLayoutPreference?: 'auto' | 'overlay' | 'side';
  heatmapScale?: 'log' | 'linear';
  searchHistory?: string[];
  sortOrder?: 'time' | 'count';
}

export interface PullResult {
  channels: SavedChannel[];
  projects: StreamProject[];
  settings: GlobalSettings | null;
}

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

/** Both this device and the cloud changed a VOD since their common version. */
export interface StateConflict {
  videoId: string;
  local: SavedState;
  remote: RemoteState;
}

class SyncConflictError extends Error {
  constructor(readonly remote: RemoteState) {
    super('Cloud copy changed on another device');
  }
}

const DEBOUNCE_MS = 30_000;
const RETRY_MS = 15_000;
const RETRYABLE_CODES = new Set(['unavailable', 'deadline-exceeded', 'aborted', 'resource-exhausted', 'internal', 'unknown', 'cancelled']);
const FIRESTORE_BATCH_LIMIT = 500;

/**
 * Cloud sync between localStorage / IndexedDB and Firestore for Pro users.
 *
 * Push methods are debounced per-entity to keep Firestore write volume bounded
 * (target: <= 1 write / 30 s per entity), and flushed immediately when the page
 * goes to the background (mobile browsers freeze timers in hidden tabs). Pull is
 * on-demand (login, open project).
 *
 * Per-VOD state writes are guarded (utils/sync-reconcile.ts `canOverwrite`):
 * each write stamps a new `rev`, and a write based on an older rev than the one
 * in the cloud becomes a StateConflict for the user to resolve instead of
 * silently replacing another device's work.
 *
 * chatComments are NEVER synced — too large; re-fetched from Twitch GQL on a new
 * device. The state-service callers strip them before invoking pushProjectState,
 * but serializeState also defends against accidental inclusion.
 */
@Injectable({ providedIn: 'root' })
export class SyncService {
  private firebaseService = inject(FirebaseService);

  syncStatus = signal<SyncStatus>('idle');
  lastSyncedAt = signal<number>(0);
  pendingChanges = signal<number>(0);
  stateConflicts = signal<StateConflict[]>([]);
  conflictDetected = computed(() => this.stateConflicts()[0] ?? null);

  // Set by TwitchChatStateService (it owns IDB + the open VOD; injecting it here
  // would be circular). Called after a state write lands, with the new cloud rev.
  onStatePushed: ((videoId: string, rev: string, stillPending: boolean) => void) | null = null;

  // Signals consumed by LibraryService / TwitchChatStateService effects.
  // pullAll populates these once per login; the consumers replace their local
  // signals with the remote payload (cloud is source-of-truth for Pro users).
  remoteChannels = signal<SavedChannel[] | null>(null);
  remoteProjects = signal<StreamProject[] | null>(null);
  remoteSettings = signal<GlobalSettings | null>(null);

  // True after pullAll has run once for the current user — gates the apply-effects
  // in consumer services so they don't clobber local data on initial app boot
  // (before login).
  hasPulled = signal<boolean>(false);

  private debounceTimers = new Map<string, any>();
  private pendingPayloads = new Map<string, () => Promise<void>>();
  // Writes in progress, per entity key — a second write of the same entity waits
  // for the first, so it is checked against the rev the first one produced.
  private inFlight = new Map<string, Promise<void>>();
  // Last successful state write per VOD: `from` base → new `to` rev. A payload
  // queued before that write landed still carries the old base; it is really
  // based on our own write, so it gets rebased instead of conflicting with itself.
  private ownWrites = new Map<string, { from: string | undefined; to: string }>();
  private lastPulledForUid: string | null = null;

  // Diagnostic kill-switch via URL: ?disableSync=1 turns off auto-pull and all
  // push effects. Used to isolate whether sync logic is what locks the UI.
  private syncDisabled = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('disableSync') === '1';

  constructor() {
    if (this.syncDisabled) {
      console.warn('🔧 SYNC DISABLED via ?disableSync=1');
      return;
    }

    if (typeof window !== 'undefined') {
      const onlineHandler = () => this.handleOnline();
      const offlineHandler = () => this.syncStatus.set('offline');
      // Push pending edits the moment the page is hidden / closed. Without this a
      // phone that switches apps within 30s of the last edit keeps it local only
      // (timers freeze in background tabs) — observed 2026-10-12: two VODs had
      // 15 + 3 moments that never reached the cloud until reopened on the phone.
      const visibilityHandler = () => {
        if (document.visibilityState === 'hidden') void this.flushAllNow();
      };
      const pagehideHandler = () => void this.flushAllNow();
      window.addEventListener('online', onlineHandler);
      window.addEventListener('offline', offlineHandler);
      document.addEventListener('visibilitychange', visibilityHandler);
      window.addEventListener('pagehide', pagehideHandler);
      if (!navigator.onLine) this.syncStatus.set('offline');

      inject(DestroyRef).onDestroy(() => {
        window.removeEventListener('online', onlineHandler);
        window.removeEventListener('offline', offlineHandler);
        document.removeEventListener('visibilitychange', visibilityHandler);
        window.removeEventListener('pagehide', pagehideHandler);
      });
    }

    // Auto-pull once per login when the user becomes sync-eligible AND has
    // already completed first-time migration (otherwise the migration UI runs first
    // and pulls afterwards as part of its finalize step).
    effect(() => {
      const profile = this.firebaseService.userProfile();
      if (!profile) {
        this.lastPulledForUid = null;
        this.hasPulled.set(false);
        return;
      }
      const eligible = profile.isPro === true
        && profile.syncEnabled !== false
        && profile.migrationCompleted === true;
      if (!eligible) return;
      if (this.lastPulledForUid === profile.uid) return;
      this.lastPulledForUid = profile.uid;
      this.pullAll(profile.uid).catch((err) => console.error('Auto pullAll failed:', err));
    });
  }

  // ============================================================
  // PULL
  // ============================================================

  async pullAll(uid: string): Promise<PullResult | null> {
    const fs = this.firebaseService.firestore;
    if (!fs) return null;

    this.syncStatus.set('syncing');
    try {
      const [channelsSnap, projectsSnap, settingsSnap] = await Promise.all([
        getDocs(collection(fs, 'users', uid, 'channels')),
        getDocs(collection(fs, 'users', uid, 'projects')),
        getDoc(doc(fs, 'users', uid, 'settings', 'global')),
      ]);

      const channels = channelsSnap.docs.map((d) => this.deserializeChannel(d.data()));
      const projects = projectsSnap.docs
        .map((d) => d.data())
        .filter((data) => !data['deletedAt']) // soft-deleted tombstones excluded
        .map((data) => this.deserializeProject(data));
      const settings = settingsSnap.exists() ? (settingsSnap.data() as GlobalSettings) : null;

      this.remoteChannels.set(channels);
      this.remoteProjects.set(projects);
      this.remoteSettings.set(settings);
      this.hasPulled.set(true);

      this.syncStatus.set('idle');
      this.lastSyncedAt.set(Date.now());

      // Best-effort: stamp last sync time on user profile (non-fatal if blocked)
      setDoc(doc(fs, 'users', uid), { lastSyncedAt: serverTimestamp() }, { merge: true })
        .catch((err) => console.warn('Could not update lastSyncedAt on user profile:', err));

      return { channels, projects, settings };
    } catch (err) {
      console.error('SyncService.pullAll failed:', err);
      this.syncStatus.set('error');
      return null;
    }
  }

  /**
   * Cloud copy of a VOD's state. null = no cloud doc. Throws when the cloud can't
   * be reached (offline, timeout, not signed in) — callers must NOT treat that as
   * "no cloud doc", or a fresh local copy would look safe to push.
   */
  async pullProjectState(videoId: string, timeoutMs = 6000): Promise<RemoteState | null> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) throw new Error('Cloud sync unavailable: not signed in');

    const snap = await Promise.race([
      getDoc(doc(fs, 'users', uid, 'projects', videoId, 'state', 'main')),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`pullProjectState(${videoId}) timed out`)), timeoutMs)),
    ]);
    return snap.exists() ? this.toRemoteState(snap.data()) : null;
  }

  /** Sync applies to this user right now (Pro, sync on, not demo, not disabled). */
  isEligible(): boolean {
    return this.guardEligible();
  }

  /** A state write for this VOD is waiting or running. */
  hasPendingState(videoId: string): boolean {
    const key = `state:${videoId}`;
    return this.pendingPayloads.has(key) || this.inFlight.has(key);
  }

  /** Drop a not-yet-started state write (the user chose the cloud copy instead). */
  cancelPendingState(videoId: string): void {
    const key = `state:${videoId}`;
    const t = this.debounceTimers.get(key);
    if (t) clearTimeout(t);
    this.debounceTimers.delete(key);
    this.pendingPayloads.delete(key);
    this.pendingChanges.set(this.pendingPayloads.size);
  }

  addConflict(conflict: StateConflict): void {
    this.stateConflicts.update(list => [...list.filter(c => c.videoId !== conflict.videoId), conflict]);
  }

  dropConflict(videoId: string): void {
    this.stateConflicts.update(list => list.filter(c => c.videoId !== videoId));
  }

  // ============================================================
  // PUSH (debounced 30s per entity)
  // ============================================================

  pushChannels(channels: SavedChannel[]): void {
    if (!this.guardEligible()) return;
    this.scheduleDebounced('channels', () => this.flushChannels(channels));
  }

  pushProject(project: StreamProject): void {
    if (!this.guardEligible()) return;
    this.scheduleDebounced(`project:${project.videoId}`, () => this.flushProject(project));
  }

  pushProjectState(videoId: string, state: SavedState, immediate = false): void {
    if (!this.guardEligible()) return;
    this.scheduleDebounced(`state:${videoId}`, () => this.flushProjectState(videoId, state), immediate ? 0 : DEBOUNCE_MS);
  }

  pushSettings(settings: GlobalSettings): void {
    if (!this.guardEligible()) return;
    this.scheduleDebounced('settings', () => this.flushSettings(settings));
  }

  /** Soft-delete: writes deletedAt tombstone immediately (cron cleanup later). */
  async deleteProjectRemote(videoId: string): Promise<void> {
    if (!this.guardEligible()) return;
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;

    // Cancel any pending push for this project — tombstone supersedes
    const stateKey = `state:${videoId}`;
    const projKey = `project:${videoId}`;
    for (const k of [stateKey, projKey]) {
      const t = this.debounceTimers.get(k);
      if (t) {
        clearTimeout(t);
        this.debounceTimers.delete(k);
        this.pendingPayloads.delete(k);
      }
    }

    try {
      this.syncStatus.set('syncing');
      await setDoc(
        doc(fs, 'users', uid, 'projects', videoId),
        { deletedAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true },
      );
      this.syncStatus.set('idle');
      this.lastSyncedAt.set(Date.now());
    } catch (err) {
      console.error(`deleteProjectRemote(${videoId}) failed:`, err);
      this.syncStatus.set('error');
    }
  }

  /**
   * Run all pending debounced writes now — page hidden / closed, back online.
   * Parallel across entities: on pagehide the browser gives us very little time.
   */
  async flushAllNow(): Promise<void> {
    const keys = Array.from(this.pendingPayloads.keys());
    for (const key of keys) {
      const timer = this.debounceTimers.get(key);
      if (timer) clearTimeout(timer);
      this.debounceTimers.delete(key);
    }
    await Promise.all(keys.map(key => this.runPayload(key)));
  }

  // ============================================================
  // INTERNAL
  // ============================================================

  private guardEligible(): boolean {
    if (this.syncDisabled) return false;
    if (checkDemoMode()) return false; // never push demo data to Firestore
    return this.firebaseService.isSyncEligible();
  }

  private scheduleDebounced(key: string, work: () => Promise<void>, delayMs = DEBOUNCE_MS): void {
    this.pendingPayloads.set(key, work); // latest write wins for this key

    const existing = this.debounceTimers.get(key);
    if (existing) clearTimeout(existing);

    this.pendingChanges.set(this.pendingPayloads.size);

    const timer = setTimeout(() => {
      this.debounceTimers.delete(key);
      void this.runPayload(key);
    }, delayMs);

    this.debounceTimers.set(key, timer);
  }

  private async runPayload(key: string): Promise<void> {
    const previous = this.inFlight.get(key);
    if (previous) await previous.catch(() => undefined);

    const payload = this.pendingPayloads.get(key);
    if (!payload) return; // already taken by a concurrent flush
    this.pendingPayloads.delete(key);
    this.pendingChanges.set(this.pendingPayloads.size);
    if (!this.guardEligible()) return; // user lost Pro mid-debounce

    this.syncStatus.set('syncing');
    const run = payload();
    this.inFlight.set(key, run);
    try {
      await run;
      this.lastSyncedAt.set(Date.now());
      if (this.pendingPayloads.size === 0 && this.inFlight.size === 1) this.syncStatus.set('idle');
    } catch (err) {
      if (err instanceof SyncConflictError) {
        // Not retried — the conflict modal decides which copy survives.
        if (this.pendingPayloads.size === 0 && this.inFlight.size === 1) this.syncStatus.set('idle');
      } else {
        console.error(`Sync flush failed for ${key}:`, err);
        this.syncStatus.set(navigator.onLine ? 'error' : 'offline');
        // Network-ish failures keep the write for a retry (unless a newer edit
        // already replaced it). permission-denied / invalid-argument won't heal.
        const code = (err as { code?: string } | null)?.code;
        const retryable = !code || RETRYABLE_CODES.has(code);
        if (retryable && !this.pendingPayloads.has(key)) this.scheduleDebounced(key, payload, RETRY_MS);
      }
    } finally {
      if (this.inFlight.get(key) === run) this.inFlight.delete(key);
    }
  }

  private async flushChannels(channels: SavedChannel[]): Promise<void> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;

    const channelsCol = collection(fs, 'users', uid, 'channels');
    const existingSnap = await getDocs(channelsCol);
    const existingIds = new Set(existingSnap.docs.map((d) => d.id));
    const newIds = new Set(channels.map((c) => c.id));

    type Op =
      | { kind: 'set'; ref: ReturnType<typeof doc>; data: any }
      | { kind: 'delete'; ref: ReturnType<typeof doc> };
    const ops: Op[] = [];
    for (const ch of channels) {
      const data = this.serializeChannel(ch);
      // Stamp addedAt only on first create — merge: true preserves existing addedAt
      // on subsequent updates (re-setting it would clobber the original add time).
      if (!existingIds.has(ch.id)) data.addedAt = serverTimestamp();
      ops.push({ kind: 'set', ref: doc(channelsCol, ch.id), data });
    }
    for (const id of existingIds) {
      if (!newIds.has(id)) ops.push({ kind: 'delete', ref: doc(channelsCol, id) });
    }

    // Firestore writeBatch caps at 500 ops; split when the combined
    // set+delete count crosses that threshold (500+ channels in library).
    for (let i = 0; i < ops.length; i += FIRESTORE_BATCH_LIMIT) {
      const slice = ops.slice(i, i + FIRESTORE_BATCH_LIMIT);
      const batch = writeBatch(fs);
      for (const op of slice) {
        if (op.kind === 'set') batch.set(op.ref, op.data, { merge: true });
        else batch.delete(op.ref);
      }
      await batch.commit();
    }
  }

  private async flushProject(project: StreamProject): Promise<void> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;
    await setDoc(
      doc(fs, 'users', uid, 'projects', project.videoId),
      this.serializeProject(project),
      { merge: true },
    );
  }

  private async flushProjectState(videoId: string, state: SavedState): Promise<void> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;
    const ref = doc(fs, 'users', uid, 'projects', videoId, 'state', 'main');
    const own = this.ownWrites.get(videoId);
    const baseRev = own && own.from === state.syncRev ? own.to : state.syncRev;
    const rev = newRev();
    // Replaces the whole doc (no merge). Sub-1MB usually; activityBins for a 4h VOD
    // with 15s bins is ~50KB, highlights ~100 entries → well under limit.
    const payload = { ...this.serializeState(state), rev };

    try {
      await runTransaction(fs, async (tx) => {
        const snap = await tx.get(ref);
        const remote = snap.exists() ? this.toRemoteState(snap.data()) : null;
        if (!canOverwrite(baseRev, remote, state)) throw new SyncConflictError(remote!);
        tx.set(ref, payload);
      });
    } catch (err) {
      if (err instanceof SyncConflictError) {
        console.warn(`Sync conflict on VOD ${videoId}: cloud rev "${err.remote.rev}", ours based on "${baseRev}"`);
        this.addConflict({ videoId, local: state, remote: err.remote });
      }
      throw err;
    }

    this.ownWrites.set(videoId, { from: baseRev, to: rev });
    this.onStatePushed?.(videoId, rev, this.pendingPayloads.has(`state:${videoId}`));
  }

  private async flushSettings(settings: GlobalSettings): Promise<void> {
    const uid = this.firebaseService.currentUser()?.uid;
    const fs = this.firebaseService.firestore;
    if (!uid || !fs) return;
    await setDoc(
      doc(fs, 'users', uid, 'settings', 'global'),
      removeUndefined({ ...settings, updatedAt: serverTimestamp() }),
      { merge: true },
    );
  }

  private async handleOnline(): Promise<void> {
    if (!this.guardEligible()) return;
    this.syncStatus.set('idle');
    // Writes that failed while offline sit in pendingPayloads with a retry timer —
    // run them now instead of waiting for it.
    await this.flushAllNow();
  }

  // ============================================================
  // SERIALIZATION (Firestore: no undefined; no nested arrays)
  // ============================================================

  private serializeChannel(c: SavedChannel): any {
    return {
      id: c.id,
      name: c.name,
      login: c.login,
      profileImageUrl: c.profileImageUrl,
      updatedAt: serverTimestamp(),
    };
  }

  private deserializeChannel(d: any): SavedChannel {
    return {
      id: d.id,
      name: d.name,
      login: d.login,
      profileImageUrl: d.profileImageUrl,
    };
  }

  private serializeProject(p: StreamProject): any {
    return removeUndefined({
      videoId: p.videoId,
      channelId: p.channelId,
      title: p.title,
      createdAt: p.createdAt,
      duration: p.duration,
      thumbnailUrl: p.thumbnailUrl,
      isChatDownloaded: !!p.isChatDownloaded,
      userColor: p.userColor ?? null,
      lastModifiedAt: p.lastModifiedAt ?? Date.now(),
      updatedAt: serverTimestamp(),
      deletedAt: null,
    });
  }

  private deserializeProject(d: any): StreamProject {
    return {
      videoId: d.videoId,
      channelId: d.channelId,
      title: d.title,
      createdAt: d.createdAt,
      duration: d.duration,
      thumbnailUrl: d.thumbnailUrl,
      isChatDownloaded: !!d.isChatDownloaded,
      chatFilePath: d.chatFilePath,
      userColor: d.userColor ?? null,
      lastModifiedAt: typeof d.lastModifiedAt === 'number' ? d.lastModifiedAt : 0,
    };
  }

  private serializeState(state: SavedState): any {
    // Strip chatComments — never sync raw chat. syncRev/syncDirty are this
    // device's bookkeeping; the cloud's own version is the `rev` field.
    const { chatComments, syncRev, syncDirty, ...rest } = state;

    const out: any = { ...rest };
    // Strip bin.messages — without this, xQc-tier chats (~100k messages
    // distributed across ~960 bins) blow past Firestore's 1MB doc limit.
    // Bins regenerate from chatComments when the receiving device
    // re-downloads chat, so we only need to preserve startTime + messageCount
    // here — enough for the heatmap to render with intensity bars while the
    // user is offered the "Скачать чат" CTA.
    if (Array.isArray(out.activityBins)) {
      out.activityBins = out.activityBins.map((b: any) => ({
        startTime: b.startTime,
        messageCount: b.messageCount,
        messages: [],
      }));
    }
    if (state.analysisCache) {
      out.analysisCache = Object.fromEntries(
        state.analysisCache.map(([k, v]) => [String(k), v]),
      );
    }
    if (state.highlightStateCache) {
      out.highlightStateCache = Object.fromEntries(
        state.highlightStateCache.map(([k, v]) => [String(k), v]),
      );
    }
    out.updatedAt = serverTimestamp();
    out.schemaVersion = SYNC_SCHEMA_VERSION;
    return removeUndefined(out);
  }

  private toRemoteState(d: any): RemoteState {
    const rev = typeof d.rev === 'string' ? d.rev : '';
    const state = this.deserializeState(d);
    state.syncRev = rev;
    state.syncDirty = false;
    const updatedAtMs = typeof d.updatedAt?.toMillis === 'function' ? d.updatedAt.toMillis() : null;
    return { state, rev, updatedAtMs };
  }

  private deserializeState(d: any): SavedState {
    const out: any = { ...d };
    delete out.updatedAt;
    delete out.schemaVersion;
    delete out.rev;

    if (d.analysisCache && typeof d.analysisCache === 'object' && !Array.isArray(d.analysisCache)) {
      out.analysisCache = Object.entries(d.analysisCache).map(([k, v]) => [Number(k), v]);
    }
    if (d.highlightStateCache && typeof d.highlightStateCache === 'object' && !Array.isArray(d.highlightStateCache)) {
      out.highlightStateCache = Object.entries(d.highlightStateCache).map(([k, v]) => [Number(k), v]);
    }
    return out as SavedState;
  }
}

// Firestore rejects undefined values anywhere in the payload — strip them.
function removeUndefined<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined) as any;
  if (typeof obj !== 'object') return obj;
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = removeUndefined(v);
  }
  return out;
}
